import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Compass, CheckCircle, Path, Plus } from "@phosphor-icons/react";
import StatusPill from "@/components/StatusPill";
import LocationBadge from "@/components/LocationBadge";

export default function MisRutas() {
  const [playbooks, setPlaybooks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pb, st] = await Promise.all([api.get("/playbooks"), api.get("/settings")]);
      setPlaybooks(pb.data.playbooks || []);
      setActiveId(st.data.active_playbook_id || null);
    } catch {
      toast.error("No se pudieron cargar tus rutas");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const choose = async (pb) => {
    setSwitching(pb.id);
    try {
      await api.put("/settings", { active_playbook_id: pb.id });
      setActiveId(pb.id);
      toast.success(`Ruta activa: ${pb.name}`);
      nav("/");
    } catch {
      toast.error("No se pudo cambiar la ruta");
    } finally { setSwitching(null); }
  };

  const progress = (p) => {
    const total = p.stages.reduce((a, s) => a + s.milestones.length, 0);
    const done = p.stages.reduce((a, s) => a + s.milestones.filter((m) => m.completed).length, 0);
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  return (
    <div data-testid="page-mis-rutas">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="min-w-0">
          <div className="label-mono text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Mis Rutas</span>
            <span aria-hidden>·</span>
            <LocationBadge variant="tag" testid="mis-rutas-location-badge" />
          </div>
          <h1 className="display text-4xl sm:text-5xl md:text-6xl mt-1 leading-[0.95]">Elige tu enfoque</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Cada ruta lleva su propio progreso, hitos y tareas. Elige en cuál trabajar ahora — puedes cambiar cuando quieras.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 self-start md:self-auto">
          <Link to="/explorar" className="btn-ghost flex items-center justify-center gap-2" data-testid="btn-explorar-rutas">
            <Plus size={14} weight="bold" /> Explorar plantillas
          </Link>
          <Link to="/brujula" className="btn-secondary flex items-center justify-center gap-2" data-testid="btn-nueva-ruta">
            <Compass size={14} weight="bold" /> Iniciar nueva ruta
          </Link>
        </div>
      </header>

      {loading && <div className="text-sm text-muted-foreground">Cargando…</div>}

      {!loading && playbooks.length === 0 && (
        <div className="brut-card" data-testid="mis-rutas-empty">
          <div className="label-mono text-muted-foreground">Tu workspace</div>
          <h2 className="display text-3xl mt-1 leading-tight">No tienes ninguna Ruta activa.</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-md leading-relaxed">
            Aquí verás tus rutas cuando empieces a construir. Puedes descubrir cuál te conviene con
            la Brújula, o elegir una del catálogo para arrancar de inmediato.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link
              to="/brujula"
              className="btn-primary flex items-center justify-center gap-2"
              data-testid="btn-empty-brujula"
            >
              <Compass size={14} weight="bold" /> Ir a Brújula
            </Link>
            <Link
              to="/explorar"
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="btn-empty-crear"
            >
              <Plus size={14} weight="bold" /> Crear Ruta
            </Link>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {playbooks.map((p) => {
          const isActive = activeId === p.id;
          const { done, total, pct } = progress(p);
          const nextStage = p.stages.find((s) => s.status !== "completada");
          const nextMs = nextStage?.milestones.find((m) => !m.completed);
          return (
            <div
              key={p.id}
              data-testid={`ruta-card-${p.id}`}
              className={`brut-card border-2 border-foreground bg-surface flex flex-col justify-between ${
                isActive ? "shadow-[6px_6px_0_0_hsl(var(--primary))]" : ""
              }`}
            >
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="label-mono text-muted-foreground">{p.category}</div>
                      {isActive && (
                        <span
                          className="label-mono flex items-center gap-1 border-2 border-foreground px-2 py-0.5"
                          style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
                          data-testid={`active-badge-${p.id}`}
                        >
                          <CheckCircle size={11} weight="fill" /> Activa
                        </span>
                      )}
                    </div>
                    <div className="display text-2xl mt-1 leading-tight break-words">{p.name}</div>
                    {p.location?.city && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {p.location.city}{p.location.country ? `, ${p.location.country}` : ""}
                      </div>
                    )}
                  </div>
                  <Path size={22} weight="bold" className="flex-shrink-0" />
                </div>

                <div className="mt-4 h-2 border-2 border-foreground">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="label-mono text-muted-foreground">{pct}% · {done}/{total} hitos</span>
                  <StatusPill value={pct === 100 ? "completada" : (pct > 0 ? "en_progreso" : "pendiente")} />
                </div>

                {nextStage && (
                  <div className="mt-4 border-t-2 border-foreground pt-3">
                    <div className="label-mono text-muted-foreground">Siguiente hito</div>
                    <div className="text-sm mt-1 font-semibold leading-snug">
                      {nextMs ? nextMs.name : `Etapa: ${nextStage.name}`}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                {isActive ? (
                  <Link
                    to="/"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid={`btn-abrir-hoy-${p.id}`}
                  >
                    Abrir Hoy <ArrowRight size={14} weight="bold" />
                  </Link>
                ) : (
                  <button
                    onClick={() => choose(p)}
                    disabled={switching === p.id}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid={`btn-hacer-activa-${p.id}`}
                  >
                    {switching === p.id ? "Cambiando…" : "Hacer activa"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                )}
                <Link
                  to={`/playbooks/${p.id}`}
                  className="btn-secondary flex items-center justify-center"
                  data-testid={`btn-ver-detalle-${p.id}`}
                >
                  Ver detalle
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
